import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { ErrorDialogComponent } from 'src/app/dialog/error-dialog/error-dialog.component';
import { Environment } from 'src/app/models/environment';
import { EnvironmentService } from 'src/app/services/environment.service';
import { OnCallService } from 'src/app/oncall/services/oncall.service';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { ToastService } from 'src/app/services/toast.service';
import { TeamMemberDetails } from 'src/app/oncall/models/team-member-details';
import { UpdateTeamMemberDialogComponent } from '../dialog/update-team-member-details-dialog/update-team-member-details-dialog.component';
import { MatSort } from '@angular/material/sort';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
    selector: 'app-oncall-team-member',
    templateUrl: './team-member.component.html',
    styleUrls: ['./team-member.component.css', '../../home/home.component.css'],
    standalone: false
})
export class TeamMemberComponent implements AfterViewInit, OnInit, OnDestroy {
  environment: Environment;
  pageEnabled: boolean;
  loading: boolean = false;
  allTeamMembers: Array<TeamMemberDetails>;
  displayedColumns: string[] = ['name', 'email', 'phoneNumber', 'action'];
  columns: any[] = [];
  dataSource!: MatTableDataSource<TeamMemberDetails>;
  currentUserEmail: string = '';
  currentUserName: string = '';
  currentUserPhoneNumber: string = '';
  searchTerm: string = '';
  isPlatform: boolean = false;
  private readonly _destroying$ = new Subject<void>();
  @ViewChild('membersTable') membersTable!: MatTable<any>;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private readonly userProfileService: UserProfileService,
    private readonly environmentService: EnvironmentService,
    private readonly onCallService: OnCallService,
    private readonly dialog: MatDialog,
    private readonly loadingService: LoadingService,
    private readonly toast: ToastService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.pageEnabled = this.environment.featureToggles.onCallEnabled === 'true';
    this.allTeamMembers = new Array<TeamMemberDetails>();
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<TeamMemberDetails>([]);
    this.columns = this.getColumns();

    // Get user information from claims
    const claims = this.userProfileService.getActiveClaims();
    if (claims) {
      this.currentUserName = claims.name as string ?? '';

      // Get user email using the new method
      this.userProfileService.getUserEmail().subscribe(email => {
        this.currentUserEmail = email;
      });

      // Check if user is a platform team member
      this.userProfileService.isPlatformTeamMember().subscribe(isPlatformMember => {
        this.isPlatform = isPlatformMember;
      });
    }

    this.populateTeamMemberDetails();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.sort.active = 'name';
      this.sort.direction = 'asc';
      this.dataSource.sort = this.sort;
    });
  }

  populateTeamMemberDetails(): void {
    this.loadingService.show('teamMembers');
    this.allTeamMembers = new Array<TeamMemberDetails>();
    this.onCallService.getAllTeamMemberDetails()
      .subscribe({
        next: (members) => {
          this.allTeamMembers = members;
          this.dataSource.data = this.allTeamMembers;
          this.loadingService.hide('teamMembers');
          this.createMemberIfNotExists(this.currentUserName, this.currentUserEmail, this.currentUserPhoneNumber);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error fetching team members');
        },
      });
  }

  createMemberIfNotExists(name: string, email: string, phoneNumber: string): void {
    let userExists = this.allTeamMembers.find(m => m.email.toLowerCase() === email.toLowerCase());
    if (!userExists) {
      this.openTeamMemberDetailsDialog(name, email, phoneNumber, true, true, true);
    }
  }

  updateTeamMemberDetails(name: string, email: string, phoneNumber: string,
    smsNotificationsEnabled: boolean, voiceCallNotificationsEnabled: boolean, emailNotificationsEnabled: boolean): void {
    this.loadingService.show('teamMembers');
    let teamMemberDetails: TeamMemberDetails = {
      name: name,
      email: email,
      phoneNumber: phoneNumber,
      smsNotificationsEnabled: smsNotificationsEnabled,
      voiceCallNotificationsEnabled: voiceCallNotificationsEnabled,
      emailNotificationsEnabled: emailNotificationsEnabled
    }
    this.onCallService.createOrUpdateTeamMemberDetails(teamMemberDetails.email, teamMemberDetails)
      .subscribe({
        next: () => {
          this.populateTeamMemberDetails();
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error creating team member');
        }
      });
  }

  deleteTeamMemberDetails(context: any, member: TeamMemberDetails): void {
    context.loadingService.show('teamMembers');
    let id = member.email;
    context.onCallService.deleteTeamMemberDetails(id)
      .subscribe({
        next: () => {
          context.teamMemberDetails = context.populateTeamMemberDetails();
          context.toast.success(`Deleted ${id}`);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error deleting team member');
        },
      });
  }

  handleError(error: HttpErrorResponse, message: string): void {
    this.loadingService.hide('teamMembers');
    this.openErrorDialog(`${message}: ${error.status} ${error.statusText}`);
  }

  applySearchFilter() {
    this.dataSource.filter = this.searchTerm;
  }

  confirmAction(
    message: string,
    confirmActionMethod: (context: any, member: TeamMemberDetails) => void,
    member: TeamMemberDetails
  ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { confirmationText: message },
    });

    dialogRef.afterClosed().subscribe({
      next: (confirmed) => {
        if (confirmed) {
          confirmActionMethod(this, member);
        }
      },
      error: () => { },
    });
  }

  getColumns(): any[] {
    let columns =
      [
        {
          columnDef: 'name',
          header: 'Name',
          cell: (member: TeamMemberDetails) => `${member.name}`        },
        {
          columnDef: 'email',
          header: 'Email',
          cell: (member: TeamMemberDetails) => `${member.email}`
        },
        {
          columnDef: 'phoneNumber',
          header: 'Phone Number',
          cell: (member: TeamMemberDetails) => `${member.phoneNumber}`
        }
      ]
    return columns;
  }

  openTeamMemberDetailsDialog(name: string, email: string, phoneNumber: string,
    smsNotificationsEnabled: boolean, voiceCallNotificationsEnabled: boolean, emailNotificationsEnabled: boolean): void {
    const dialogRef = this.dialog.open(UpdateTeamMemberDialogComponent, {
      data: {
        name: name,
        email: email,
        phoneNumber: phoneNumber,
        smsNotifications: smsNotificationsEnabled,
        emailNotifications: emailNotificationsEnabled,
        voiceCallNotifications: voiceCallNotificationsEnabled
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (data) => {
        data.phoneNumber = '1' + data.phoneNumber;
        this.updateTeamMemberDetails(data.name, data.email, data.phoneNumber,
          data.smsNotificationsEnabled, data.voiceCallNotificationsEnabled, data.emailNotificationsEnabled);
      },
      error: () => { },
    });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      data: { message: errorMessage }
    });
  }
}
